// ZZYON LEX — aba Contratos.
// Fonte: themis.contratos_indice + contratos_docs/aditivos/obrigacoes
// (pipeline scan_juridico.py + triagem_contratos.py sobre a pasta "12. JURIDICO").

import { lex } from './lex-client.js';
import { fmtBRL, fmtDate, fmtDateTime, escapeHtml } from './utils.js';

const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

// ─── Fetchers ──────────────────────────────────────────────────────
async function fetchDashboard() {
  const { data, error } = await lex.from('vw_contratos_dashboard').select('*').maybeSingle();
  if (error) throw error;
  return data;
}

async function fetchContratos() {
  const { data, error } = await lex
    .from('contratos_indice')
    .select('id,titulo,contraparte,categoria,papel_icaro,filial_codigo,valor_mensal,vigencia_inicio,vigencia_fim,renovacao_automatica,risco_nivel,analise_confianca,precisa_revisao_humana,revisado_em,status,pasta_origem')
    .order('atualizado_em', { ascending: false })
    .limit(500);
  if (error) throw error;
  return data || [];
}

async function fetchContrato(id) {
  const { data, error } = await lex.from('contratos_indice').select('*').eq('id', id).maybeSingle();
  if (error) throw error;
  return data;
}

async function fetchObrigacoes(contratoId) {
  const { data, error } = await lex
    .from('contratos_obrigacoes')
    .select('*')
    .eq('contrato_id', contratoId)
    .order('tipo');
  if (error) throw error;
  return data || [];
}

async function fetchAditivos(contratoId) {
  const { data, error } = await lex
    .from('contratos_aditivos')
    .select('*')
    .eq('contrato_id', contratoId)
    .order('data_assinatura', { ascending: false, nullsFirst: false });
  if (error) throw error;
  return data || [];
}

async function fetchDocsDoContrato(contratoId) {
  const { data, error } = await lex
    .from('contratos_docs')
    .select('id,nome_arquivo,caminho,tipo_doc,paginas,texto_chars,status_processamento,modificado_em')
    .eq('contrato_id', contratoId)
    .order('tamanho_bytes', { ascending: false })
    .limit(100);
  if (error) throw error;
  return data || [];
}

async function fetchKpiStatus() {
  const { data, error } = await lex.from('vw_contratos_kpi_status').select('*').limit(300);
  if (error) throw error;
  return data || [];
}

async function marcarRevisado(id, email) {
  const { error } = await lex
    .from('contratos_indice')
    .update({ revisado_por: email, revisado_em: new Date().toISOString(), precisa_revisao_humana: false })
    .eq('id', id);
  if (error) throw error;
}

// ─── Badges ────────────────────────────────────────────────────────
function janelaBadge(vigenciaFim) {
  if (!vigenciaFim) return { cls: 'lex-badge lex-badge-info', txt: 'SEM VIGÊNCIA' };
  const dias = Math.floor((new Date(vigenciaFim) - Date.now()) / 86_400_000);
  if (dias < 0)   return { cls: 'lex-badge lex-badge-crit',   txt: `VENCIDO ${fmtDate(vigenciaFim)}` };
  if (dias <= 30) return { cls: 'lex-badge lex-badge-crit',   txt: `${dias} DIAS` };
  if (dias <= 60) return { cls: 'lex-badge lex-badge-warn',   txt: `${dias} DIAS` };
  if (dias <= 90) return { cls: 'lex-badge lex-badge-bronze', txt: `${dias} DIAS` };
  return { cls: 'lex-badge lex-badge-ok', txt: fmtDate(vigenciaFim) };
}

function riscoContratoBadge(nivel) {
  const map = {
    critico: { cls: 'lex-badge lex-badge-crit',   txt: 'CRÍTICO' },
    alto:    { cls: 'lex-badge lex-badge-crit',   txt: 'ALTO' },
    medio:   { cls: 'lex-badge lex-badge-warn',   txt: 'MÉDIO' },
    baixo:   { cls: 'lex-badge lex-badge-ok',     txt: 'BAIXO' },
  };
  return map[nivel] || { cls: 'lex-badge lex-badge-info', txt: '—' };
}

function kpiStatusBadge(status) {
  const map = {
    dentro:        { cls: 'lex-badge lex-badge-ok',     txt: 'DENTRO' },
    atencao:       { cls: 'lex-badge lex-badge-warn',   txt: 'ATENÇÃO' },
    fora:          { cls: 'lex-badge lex-badge-crit',   txt: 'FORA' },
    nao_avaliado:  { cls: 'lex-badge lex-badge-info',   txt: 'NÃO AVALIADO' },
    nao_aplicavel: { cls: 'lex-badge lex-badge-info',   txt: 'N/A' },
  };
  return map[status] || map.nao_avaliado;
}

const CATEGORIAS = {
  cliente: 'Cliente', fornecedor: 'Fornecedor', locacao: 'Locação',
  parceiro_logistico: 'Parceiro logístico', financeiro: 'Financeiro',
  adesao_servico: 'Adesão/serviço', trabalhista_pj: 'Trabalhista/PJ',
  comodato: 'Comodato', franquia: 'Franquia', distrato: 'Distrato', outro: 'Outro',
};

// ─── Lista ─────────────────────────────────────────────────────────
export async function renderContratos(main) {
  const [dash, contratos, kpis] = await Promise.all([
    fetchDashboard().catch(() => null),
    fetchContratos(),
    fetchKpiStatus().catch(() => []),
  ]);

  const kpisFora = kpis.filter(k => k.status === 'fora').length;
  const kpisAval = kpis.filter(k => k.status !== 'nao_avaliado').length;

  main.innerHTML = `
    <div class="lex-grid-cards">
      <div class="lex-card accent">
        <div class="label">Contratos mapeados</div>
        <div class="value">${dash?.total_contratos ?? contratos.length}</div>
        <div class="sub">Pasta 12. JURIDICO — pipeline IA</div>
      </div>
      <div class="lex-card">
        <div class="label">Valor mensal sob gestão</div>
        <div class="value">${fmtBRL(dash?.valor_mensal_sob_gestao)}</div>
        <div class="sub">Soma dos contratos vigentes com valor extraído</div>
      </div>
      <div class="lex-card">
        <div class="label">Vencidos / sem vigência</div>
        <div class="value">${(dash?.vencidos ?? 0)} / ${(dash?.sem_vigencia ?? 0)}</div>
        <div class="sub">Requerem regularização ou aditivo</div>
      </div>
      <div class="lex-card">
        <div class="label">Vencem em ≤90 dias</div>
        <div class="value">${dash?.vencem_90d ?? 0}</div>
        <div class="sub">Janela de renegociação aberta</div>
      </div>
      <div class="lex-card">
        <div class="label">Risco alto/crítico</div>
        <div class="value">${dash?.risco_alto ?? 0}</div>
        <div class="sub">Classificação da triagem IA</div>
      </div>
      <div class="lex-card">
        <div class="label">Aguardando revisão humana</div>
        <div class="value">${dash?.aguardando_revisao ?? 0}</div>
        <div class="sub">Fila de escalonamento THEMIS</div>
      </div>
    </div>

    ${kpis.length ? `
    <div class="lex-section">
      <h2>Contrato × Realidade <span class="count">${kpis.length} KPIs/SLAs</span></h2>
      <div style="font-size:12px;color:rgba(255,255,255,0.55);margin-bottom:10px">
        Metas extraídas das cláusulas vs. desempenho operacional (ZYONCS).
        ${kpisAval ? `${kpisFora} fora da meta.` : 'Cruzamento operacional em implantação — metas já mapeadas abaixo.'}
      </div>
      <table class="lex-table">
        <thead><tr>
          <th>Contrato</th><th>Tipo</th><th>Indicador</th><th>Meta (cláusula)</th>
          <th>Realizado</th><th>Status</th>
        </tr></thead>
        <tbody>
          ${kpis.slice(0, 30).map(k => {
            const b = kpiStatusBadge(k.status);
            return `
              <tr data-contrato="${k.contrato_id}">
                <td><strong>${escapeHtml(k.contraparte || k.contrato_titulo || '—')}</strong></td>
                <td>${escapeHtml((k.tipo || '').toUpperCase())}</td>
                <td>${escapeHtml(k.descricao)}</td>
                <td>${escapeHtml(k.meta_valor || '—')}${k.fonte_clausula ? ` <span style="color:rgba(255,255,255,0.4);font-size:11px">(${escapeHtml(k.fonte_clausula)})</span>` : ''}</td>
                <td>${k.realizado_valor != null ? escapeHtml(String(k.realizado_valor)) + (k.unidade === '%' ? '%' : '') : '—'}</td>
                <td><span class="${b.cls}">${b.txt}</span></td>
              </tr>`;
          }).join('')}
        </tbody>
      </table>
    </div>` : ''}

    <div class="lex-toolbar" style="display:flex;gap:10px;flex-wrap:wrap;align-items:center">
      <input id="ctr-busca" class="lex-input" style="max-width:260px;margin:0" placeholder="Buscar contraparte..." />
      <select id="ctr-janela" class="lex-input" style="max-width:190px;margin:0">
        <option value="">Vigência: todas</option>
        <option value="vencido">Vencidos</option>
        <option value="d90">Vencem ≤90 dias</option>
        <option value="sem">Sem vigência extraída</option>
        <option value="ok">Vigentes &gt;90 dias</option>
      </select>
      <select id="ctr-categoria" class="lex-input" style="max-width:190px;margin:0">
        <option value="">Categoria: todas</option>
        ${Object.entries(CATEGORIAS).map(([v, l]) => `<option value="${v}">${l}</option>`).join('')}
      </select>
      <select id="ctr-revisao" class="lex-input" style="max-width:210px;margin:0">
        <option value="">Revisão: todas</option>
        <option value="pendente">Aguardando revisão humana</option>
        <option value="revisado">Revisados</option>
      </select>
    </div>

    <div class="lex-section">
      <h2>Contratos <span class="count" id="ctr-count">${contratos.length}</span></h2>
      <div id="ctr-tabela"></div>
    </div>
  `;

  function aplicaFiltros() {
    const busca = ($('#ctr-busca', main)?.value || '').toLowerCase();
    const janela = $('#ctr-janela', main)?.value || '';
    const cat = $('#ctr-categoria', main)?.value || '';
    const rev = $('#ctr-revisao', main)?.value || '';
    const agora = Date.now();

    const rows = contratos.filter(c => {
      if (busca && !(`${c.contraparte} ${c.titulo} ${c.pasta_origem}`.toLowerCase().includes(busca))) return false;
      if (cat && c.categoria !== cat) return false;
      if (rev === 'pendente' && !c.precisa_revisao_humana) return false;
      if (rev === 'revisado' && !c.revisado_em) return false;
      if (janela) {
        const fim = c.vigencia_fim ? new Date(c.vigencia_fim).getTime() : null;
        const dias = fim != null ? Math.floor((fim - agora) / 86_400_000) : null;
        if (janela === 'vencido' && !(dias != null && dias < 0)) return false;
        if (janela === 'd90' && !(dias != null && dias >= 0 && dias <= 90)) return false;
        if (janela === 'sem' && fim != null) return false;
        if (janela === 'ok' && !(dias != null && dias > 90)) return false;
      }
      return true;
    });

    $('#ctr-count', main).textContent = rows.length;
    $('#ctr-tabela', main).innerHTML = rows.length ? `
      <table class="lex-table">
        <thead><tr>
          <th>Contraparte</th><th>Categoria</th><th>Filial</th>
          <th>Valor mensal</th><th>Vigência fim</th><th>Renov. auto</th>
          <th>Risco</th><th>IA</th><th>Revisão</th>
        </tr></thead>
        <tbody>
          ${rows.map(c => {
            const j = janelaBadge(c.vigencia_fim);
            const r = riscoContratoBadge(c.risco_nivel);
            return `
              <tr data-id="${c.id}">
                <td><strong>${escapeHtml(c.contraparte || c.pasta_origem || '—')}</strong><br>
                    <span style="font-size:11px;color:rgba(255,255,255,0.45)">${escapeHtml(c.titulo || '')}</span></td>
                <td>${escapeHtml(CATEGORIAS[c.categoria] || '—')}</td>
                <td>${escapeHtml(c.filial_codigo || '—')}</td>
                <td>${fmtBRL(c.valor_mensal)}</td>
                <td><span class="${j.cls}">${j.txt}</span></td>
                <td>${c.renovacao_automatica === true ? 'Sim' : c.renovacao_automatica === false ? 'Não' : '—'}</td>
                <td><span class="${r.cls}">${r.txt}</span></td>
                <td>${c.analise_confianca != null ? Math.round(c.analise_confianca * 100) + '%' : '—'}</td>
                <td>${c.revisado_em
                      ? '<span class="lex-badge lex-badge-ok">REVISADO</span>'
                      : c.precisa_revisao_humana
                        ? '<span class="lex-badge lex-badge-warn">PENDENTE</span>'
                        : '<span class="lex-badge lex-badge-info">AUTO</span>'}</td>
              </tr>`;
          }).join('')}
        </tbody>
      </table>` : '<div class="lex-empty">Nenhum contrato com esses filtros.</div>';

    $$('#ctr-tabela tr[data-id]', main).forEach(tr =>
      tr.addEventListener('click', () => { location.hash = `contratos/${tr.dataset.id}`; })
    );
  }

  ['ctr-busca', 'ctr-janela', 'ctr-categoria', 'ctr-revisao'].forEach(id =>
    $(`#${id}`, main)?.addEventListener(id === 'ctr-busca' ? 'input' : 'change', aplicaFiltros)
  );
  aplicaFiltros();

  $$('[data-contrato]', main).forEach(tr =>
    tr.addEventListener('click', () => { location.hash = `contratos/${tr.dataset.contrato}`; })
  );
}

// ─── Detalhe ───────────────────────────────────────────────────────
export async function renderContratoDetalhe(main, id, user) {
  const contrato = await fetchContrato(id);
  if (!contrato) {
    main.innerHTML = '<div class="lex-empty">Contrato não encontrado.</div>';
    return;
  }
  const [obrigacoes, aditivos, docs] = await Promise.all([
    fetchObrigacoes(id), fetchAditivos(id), fetchDocsDoContrato(id),
  ]);

  const j = janelaBadge(contrato.vigencia_fim);
  const r = riscoContratoBadge(contrato.risco_nivel);
  const clausulas = Array.isArray(contrato.clausulas_criticas) ? contrato.clausulas_criticas : [];

  const campo = (label, valor) => `
    <div>
      <div style="font-size:11px;text-transform:uppercase;letter-spacing:0.06em;color:rgba(255,255,255,0.45)">${label}</div>
      <div style="font-size:14px;margin-top:2px">${valor}</div>
    </div>`;

  main.innerHTML = `
    <div class="lex-toolbar" style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:10px">
      <button class="lex-btn-secondary" id="ctr-voltar">&larr; Contratos</button>
      <div style="display:flex;gap:8px;align-items:center">
        <span class="${j.cls}">${j.txt}</span>
        <span class="${r.cls}">RISCO ${r.txt}</span>
        ${contrato.revisado_em
          ? `<span class="lex-badge lex-badge-ok">REVISADO ${fmtDate(contrato.revisado_em)}</span>`
          : contrato.precisa_revisao_humana
            ? '<span class="lex-badge lex-badge-warn">AGUARDA REVISÃO HUMANA</span>' : ''}
        ${!contrato.revisado_em ? '<button class="lex-btn-secondary" id="ctr-revisar">Marcar como revisado</button>' : ''}
      </div>
    </div>

    <div class="lex-section">
      <h2>${escapeHtml(contrato.contraparte || contrato.pasta_origem || 'Contrato')}</h2>
      <div style="font-size:13px;color:rgba(255,255,255,0.6);margin-bottom:16px">${escapeHtml(contrato.titulo || '')}</div>
      <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:14px">
        ${campo('Categoria', escapeHtml(CATEGORIAS[contrato.categoria] || '—'))}
        ${campo('Papel da Ícaro', escapeHtml(contrato.papel_icaro || '—'))}
        ${campo('CNPJ contraparte', escapeHtml(contrato.contraparte_cnpj || '—'))}
        ${campo('Filial', escapeHtml(contrato.filial_codigo || '—'))}
        ${campo('Vigência', `${fmtDate(contrato.vigencia_inicio)} → ${fmtDate(contrato.vigencia_fim)}`)}
        ${campo('Renovação automática', contrato.renovacao_automatica === true ? 'Sim' : contrato.renovacao_automatica === false ? 'Não' : '—')}
        ${campo('Prazo de denúncia', contrato.prazo_denuncia_dias ? `${contrato.prazo_denuncia_dias} dias` : '—')}
        ${campo('Valor mensal', fmtBRL(contrato.valor_mensal))}
        ${campo('Valor total', fmtBRL(contrato.valor_total))}
        ${campo('Reajuste', escapeHtml([contrato.reajuste_indice, contrato.reajuste_periodicidade].filter(Boolean).join(' · ') || '—'))}
        ${campo('Multa rescisória', escapeHtml(contrato.multa_rescisoria || '—'))}
        ${campo('Foro', escapeHtml(contrato.foro || '—'))}
      </div>
      ${contrato.objeto ? `
        <div style="margin-top:16px;padding:12px;border-left:2px solid var(--lex-bronze);background:rgba(140,107,63,0.08);font-size:13px">
          ${escapeHtml(contrato.objeto)}
        </div>` : ''}
      ${contrato.risco_justificativa ? `
        <div style="margin-top:10px;font-size:12px;color:rgba(255,255,255,0.55)">
          <strong>Justificativa do risco:</strong> ${escapeHtml(contrato.risco_justificativa)}
        </div>` : ''}
    </div>

    ${clausulas.length ? `
    <div class="lex-section">
      <h2>Cláusulas críticas <span class="count">${clausulas.length}</span></h2>
      <table class="lex-table">
        <thead><tr><th>Tema</th><th>Resumo</th><th>Cláusula</th></tr></thead>
        <tbody>
          ${clausulas.map(c => `
            <tr>
              <td><span class="lex-badge lex-badge-bronze">${escapeHtml((c.tema || '—').toUpperCase())}</span></td>
              <td>${escapeHtml(c.resumo || '—')}</td>
              <td>${escapeHtml(c.clausula || '—')}</td>
            </tr>`).join('')}
        </tbody>
      </table>
    </div>` : ''}

    ${obrigacoes.length ? `
    <div class="lex-section">
      <h2>Obrigações e KPIs <span class="count">${obrigacoes.length}</span></h2>
      <table class="lex-table">
        <thead><tr>
          <th>Tipo</th><th>Descrição</th><th>Meta</th><th>Realizado</th><th>Fonte</th><th>Status</th>
        </tr></thead>
        <tbody>
          ${obrigacoes.map(o => {
            const b = kpiStatusBadge(o.status);
            return `
              <tr>
                <td>${escapeHtml((o.tipo || '').toUpperCase())}</td>
                <td>${escapeHtml(o.descricao)}</td>
                <td>${escapeHtml(o.meta_valor || '—')}</td>
                <td>${o.realizado_valor != null ? escapeHtml(String(o.realizado_valor)) : '—'}</td>
                <td>${escapeHtml(o.fonte_clausula || o.realizado_fonte || '—')}</td>
                <td><span class="${b.cls}">${b.txt}</span></td>
              </tr>`;
          }).join('')}
        </tbody>
      </table>
    </div>` : ''}

    ${aditivos.length ? `
    <div class="lex-section">
      <h2>Aditivos e distratos <span class="count">${aditivos.length}</span></h2>
      <table class="lex-table">
        <thead><tr><th>Tipo</th><th>Documento</th><th>Assinatura</th><th>Nova vigência</th><th>Novo valor</th><th>Objeto</th></tr></thead>
        <tbody>
          ${aditivos.map(a => `
            <tr>
              <td><span class="lex-badge ${a.tipo === 'distrato' ? 'lex-badge-crit' : 'lex-badge-bronze'}">${escapeHtml((a.tipo || '—').toUpperCase())}</span></td>
              <td>${escapeHtml(a.numero || '—')}</td>
              <td>${fmtDate(a.data_assinatura)}</td>
              <td>${fmtDate(a.altera_vigencia_fim)}</td>
              <td>${fmtBRL(a.altera_valor)}</td>
              <td>${escapeHtml(a.objeto || '—')}</td>
            </tr>`).join('')}
        </tbody>
      </table>
    </div>` : ''}

    <div class="lex-section">
      <h2>Documentos do dossiê <span class="count">${docs.length}</span></h2>
      ${docs.length ? `
      <table class="lex-table">
        <thead><tr><th>Arquivo</th><th>Tipo</th><th>Páginas</th><th>Status</th><th>Modificado</th></tr></thead>
        <tbody>
          ${docs.map(d => `
            <tr title="${escapeHtml(d.caminho)}">
              <td>${escapeHtml(d.nome_arquivo)}</td>
              <td>${escapeHtml(d.tipo_doc || '—')}</td>
              <td>${d.paginas ?? '—'}</td>
              <td>${escapeHtml(d.status_processamento)}</td>
              <td>${fmtDate(d.modificado_em)}</td>
            </tr>`).join('')}
        </tbody>
      </table>` : '<div class="lex-empty">Nenhum documento vinculado.</div>'}
      <div style="font-size:11px;color:rgba(255,255,255,0.4);margin-top:8px">
        Pasta física: 12. JURIDICO/5. Contratos/${escapeHtml(contrato.pasta_origem || '')}
      </div>
    </div>

    <div style="font-size:11px;color:rgba(255,255,255,0.4);padding:10px 4px 30px">
      Extração automática ${escapeHtml(contrato.analise_modelo || '')} em ${fmtDateTime(contrato.analise_em)}
      · confiança ${contrato.analise_confianca != null ? Math.round(contrato.analise_confianca * 100) + '%' : '—'}.
      Análise preliminar, consulte advogado para decisões.
    </div>
  `;

  $('#ctr-voltar', main)?.addEventListener('click', () => { location.hash = 'contratos'; });
  $('#ctr-revisar', main)?.addEventListener('click', async () => {
    try {
      await marcarRevisado(id, user?.email || null);
      renderContratoDetalhe(main, id, user);
    } catch (err) {
      alert('Não foi possível marcar como revisado: ' + (err.message || err));
    }
  });
}
